import { Check, Xmark } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { Stack, Text } from 'ui';
import { useCurrentJourney } from '~/stores/journeyStore';
import { richLink } from '~/utils/richTextTags';
import { SaveButton } from '../Common/SaveButton';
import ShareButton from '../Common/ShareButton/src/ShareButton';
import { Disclosure } from '../Disclosure';
import JourneyCarbonFootprint from './JourneyCarbonFootprint';

type JourneyIncludesProps = {
  variant?: 'journey' | 'checkout' | 'journey-mobile';
  details?: {
    tripInDays: number;
  };
  uniqueId?: string;
};

const faqLink = richLink({ href: '/about/byway-faqs', hasUnderline: true, target: '_self' });

const WHATS_INCLUDED = [
  { labelKey: 'includes_transport' },
  { labelKey: 'includes_accommodation' },
  { labelKey: 'includes_package_protection' },
  { labelKey: 'includes_disruption_guarantee' },
  { labelKey: 'includes_support' },
  { labelKey: 'includes_accessibility' }
] as const;

const WHATS_NOT_INCLUDED = [
  { labelKey: 'excludes_insurance' },
  { labelKey: 'excludes_city_taxes' },
  { labelKey: 'excludes_seats' },
  { labelKey: 'excludes_luggage' }
] as const;

function JourneyIncludesWithJourney({ variant, details, uniqueId }: JourneyIncludesProps) {
  const journey = useCurrentJourney();
  const t = useTranslations('journey');
  const tripInDays = journey?.details?.tripInDays || details?.tripInDays;
  const showSaveShareNav = process.env.NEXT_PUBLIC_FF_TRIP_SAVE_SHARE_NAV === 'true';

  return (
    <>
      {variant === 'journey' && (
        <div className="hidden md:block">
          <Stack horizontal justify="between" align="center">
            <Stack horizontal spacing={2} align="center" className="pb-4">
              <Text as="span" variant="subtitle-2" data-test-id="journey-duration">
                {t('days_count', { count: tripInDays ?? 0 })}
              </Text>
              <JourneyCarbonFootprint />
            </Stack>
            {!showSaveShareNav && (
              <Stack horizontal spacing={4} className="pb-4">
                <ShareButton />
                {uniqueId && (
                  <SaveButton
                    tripId={uniqueId}
                    startDate={journey?.details?.startDate}
                    endDate={journey?.details?.endDate}
                  />
                )}
              </Stack>
            )}
          </Stack>
          <Stack spacing={3}>
            <Stack horizontal spacing={2}>
              <Text as="span" variant="emphasis-2" className="mr-2 whitespace-nowrap">
                {t('includes_label')}
              </Text>
              <ul className="grid grid-cols-3 gap-3 text-xs lg:gap-4 lg:flex">
                {WHATS_INCLUDED.map(({ labelKey }) => (
                  <li key={`included-${labelKey}`} className="flex items-center gap-1">
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 bg-green-800 rounded-full">
                      <Check decorative color="var(--by-color-icon-contrast)" size="scale" />
                    </div>
                    <span>{t(labelKey)}</span>
                  </li>
                ))}
              </ul>
            </Stack>
          </Stack>
        </div>
      )}
      {variant === 'journey-mobile' && (
        <div className="pl-4 pr-2 mt-5 md:hidden">
          <Stack spacing={3}>
            <Stack spacing={3}>
              <Text as="span" variant="emphasis-2" className="mr-2 whitespace-nowrap">
                {t('includes_label')}
              </Text>
              <ul className="flex flex-wrap gap-4 text-xs">
                {WHATS_INCLUDED.map(({ labelKey }) => (
                  <li key={`included-${labelKey}`} className="flex items-center gap-1">
                    <div className="flex items-center justify-center flex-shrink-0 w-5 h-5 bg-green-800 rounded-full">
                      <Check decorative color="var(--by-color-icon-contrast)" size="scale" />
                    </div>
                    <span>{t(labelKey)}</span>
                  </li>
                ))}
              </ul>
            </Stack>
          </Stack>
        </div>
      )}
    </>
  );
}

function JourneyIncludesCheckout({ uniqueId }: Pick<JourneyIncludesProps, 'uniqueId'>) {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const commonT = useTranslations('common');

  return (
    <div>
      <Disclosure
        defaultExpanded
        title={t('includes_title')}
        headingLevel="h2"
        size="lg"
        headerColour="blueSecondary"
        showChevron
        noBorder
      >
        <ul className="flex flex-col gap-3 lg:gap-4 lg:flex">
          {WHATS_INCLUDED.map(({ labelKey }) => (
            <li key={`included-${labelKey}`} className="flex items-center gap-3">
              <div className="flex items-center justify-center w-5 h-5 text-white bg-green-800 rounded-full">
                <Check ariaLabel={commonT('included')} size="scale" strokeWidth={2} />
              </div>
              <span>{t(labelKey)}</span>
            </li>
          ))}
          {WHATS_NOT_INCLUDED.map(({ labelKey }) => (
            <li key={`not-included-${labelKey}`} className="flex items-center gap-3">
              <div className="flex items-center justify-center w-5 h-5 text-black border rounded-full shrink-0">
                <Xmark size="scale" ariaLabel={tAria('not_included')} />
              </div>
              <span className="line-through">{t(labelKey)}</span>
            </li>
          ))}
        </ul>
        <Text className="pt-4">
          {t.rich('includes_luggage_note', {
            faqLink,
            conciergeLink: richLink({
              href: uniqueId ? `/concierge?tripId=${uniqueId}` : '/concierge',
              hasUnderline: true,
              target: '_self'
            })
          })}
        </Text>
      </Disclosure>
    </div>
  );
}

export default function JourneyIncludes({ variant = 'journey', details, uniqueId }: JourneyIncludesProps) {
  if (variant === 'checkout') {
    return <JourneyIncludesCheckout uniqueId={uniqueId} />;
  }

  return <JourneyIncludesWithJourney variant={variant} details={details} uniqueId={uniqueId} />;
}
